import React, { useState } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import './DeleteAccountModal.css';

const DeleteAccountModal = ({ onClose, onConfirm, isDeleting, errorMessage, ownedSocieties = [] }) => {
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState('confirm');
  const canProceed = confirmText === 'DELETE';

  const advanceFromConfirm = () => {
    if (ownedSocieties.length > 0) {
      setStep('ownership');
    } else {
      setStep('final');
    }
  };

  if (step === 'ownership') {
    return (
      <div className="dam-overlay">
        <div className="dam-sheet" onClick={e => e.stopPropagation()}>
          <button className="dam-close" onClick={onClose}>
            <FiX size={20} />
          </button>

          <div className="dam-icon-wrap">
            <FiAlertTriangle size={28} className="dam-icon" />
          </div>

          <h2 className="dam-title">You own {ownedSocieties.length === 1 ? 'a society' : 'societies'}</h2>
          <p className="dam-subtitle">Here is what will happen to each society.</p>

          <div className="dam-section">
            <ul className="dam-ownership-list">
              {ownedSocieties.map(s => (
                <li key={s.id} className={`dam-ownership-item ${s.successor ? 'dam-ownership-item--transfer' : 'dam-ownership-item--archive'}`}>
                  <span className="dam-ownership-society">{s.name}</span>
                  <span className="dam-ownership-outcome">
                    {s.successor
                      ? `Ownership will transfer to ${s.successor.display_name}`
                      : 'No other admins — society will be shut down'}
                  </span>
                </li>
              ))}
            </ul>
            {ownedSocieties.some(s => !s.successor) && (
              <p className="dam-body" style={{ marginTop: 12 }}>
                To keep a society running, go back and promote another member to admin first.
              </p>
            )}
          </div>

          <div className="dam-actions">
            <button className="dam-btn dam-btn--cancel" onClick={() => setStep('confirm')}>
              Go Back
            </button>
            <button className="dam-btn dam-btn--delete" onClick={() => setStep('final')}>
              Continue Anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'final') {
    return (
      <div className="dam-overlay">
        <div className="dam-sheet" onClick={e => e.stopPropagation()}>
          <button className="dam-close" onClick={onClose} disabled={isDeleting}>
            <FiX size={20} />
          </button>

          <div className="dam-icon-wrap">
            <FiAlertTriangle size={28} className="dam-icon" />
          </div>

          <h2 className="dam-title">Last chance</h2>
          <p className="dam-subtitle">Your account will be permanently deleted.</p>

          <div className="dam-section">
            <p className="dam-body">
              Once you confirm, you will be logged out immediately and your login will stop working. This cannot be reversed.
            </p>
            <p className="dam-body">
              Your scoring history and leaderboard positions will remain intact, with your name replaced by "Deleted Player".
            </p>
          </div>

          {errorMessage && (
            <div className="dam-error">{errorMessage}</div>
          )}

          <div className="dam-actions">
            <button className="dam-btn dam-btn--cancel" onClick={() => setStep(ownedSocieties.length > 0 ? 'ownership' : 'confirm')} disabled={isDeleting}>
              Go Back
            </button>
            <button
              className="dam-btn dam-btn--delete"
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Yes, Delete My Account'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dam-overlay" onClick={onClose}>
      <div className="dam-sheet" onClick={e => e.stopPropagation()}>
        <button className="dam-close" onClick={onClose} disabled={isDeleting}>
          <FiX size={20} />
        </button>

        <div className="dam-icon-wrap">
          <FiAlertTriangle size={28} className="dam-icon" />
        </div>

        <h2 className="dam-title">Delete Account</h2>
        <p className="dam-subtitle">This action is permanent and cannot be undone.</p>

        <div className="dam-section">
          <p className="dam-section-label">What will be removed:</p>
          <ul className="dam-list dam-list--red">
            <li>Your login and access to all societies</li>
            <li>Your personal account details</li>
            <li>Your society memberships</li>
          </ul>
        </div>

        <div className="dam-section">
          <p className="dam-section-label">What will be preserved:</p>
          <ul className="dam-list dam-list--green">
            <li>Your scoring history and round records</li>
            <li>Your positions on past tournament leaderboards</li>
            <li>Society handicap history</li>
          </ul>
          <p className="dam-note">
            Your player record will be anonymised — your name is replaced with
            "Deleted Player" and removed from all selection lists and player lookups.
            Historical scores remain intact so the society's records stay complete.
          </p>
        </div>

        <div className="dam-confirm-section">
          <label className="dam-confirm-label">
            Type <strong>DELETE</strong> to confirm
          </label>
          <input
            className="dam-confirm-input"
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="DELETE"
            autoCapitalize="characters"
          />
        </div>

        <div className="dam-actions">
          <button className="dam-btn dam-btn--cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="dam-btn dam-btn--delete"
            onClick={advanceFromConfirm}
            disabled={!canProceed}
          >
            Delete My Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
